<template lang="pug">
div(v-if="contentpage")
	h1 {{contentpage.name}}
	ws-content(:value="contentpage.content")
		template(slot="webcam-list")
			area-webcam-list(:items="area.webcams")
		template(slot="webcam-map")
			client-only
				mapbox.mt-6(
					ref="map"
					style="height:600px"
					:layers="layers"
					:sources="sources"
					:fitbounds="markers.features.map(e=>e.geometry.coordinates)"
					:mapOptions="mapOptions"
					@map-styleload="mapStyle")
					div(slot="popup")
						span.h3 {{popupData.name}}
		template(slot="checklist")
			ws-checklist(:area-id="area.id")
	template
		h2 {{$t('travelbox.title_{destination}', {destination: area.name})}}
		travelbox(:destination-slug="area.slug" destination-type="area")
</template>

<script>
import gql from 'graphql-tag';
import { ref } from '@vue/composition-api';
import ResortTable from '@/components/resort-table.vue';
import AreaWebcamList from '@/components/areas/webcam-list.vue';
import MetaFragment from '@/components/graphql/metaFragment.graphql';
import useMap from '@/use/map';
import Travelbox from '@/components/travelbox.vue';

const areaWebcams = gql`
	query areaWebcams($slug: String, $path: String, $fallback: String) {
		contentpage(path: $path, fallback: $fallback) {
			id
			name
			slug
			content {
				markdown
			}
			...MetaFragment
		}
		area(slug: $slug) {
			id
			name
			slug
			country {
				id
				name
				countryCode
				slug
			}
			region {
				id
				name
				slug
			}
			webcams {
				name
				id
				elevation
				preview
				location
			}
		}
	}
	${MetaFragment}
`;

export default {
	nuxtI18n: {
		paths: {
			'nl-NL': '/skigebieden/:area/webcams',
			'de-DE': '/skigebiete/:area/webcams',
			'nl-BE': '/skigebieden/:area/webcams'
		}
	},
	components: {
		ResortTable,
		AreaWebcamList,
		Travelbox,
		Mapbox: () => (process.client ? import('@/components/mapbox.vue') : null)
	},
	layout: 'default',
	setup() {
		const mapItems = ref([]);
		const { markers, sources, attachListeners, popupData, mapOptions } =
			useMap(mapItems);

		const layers = [
			{
				id: 'markers',
				type: 'symbol',
				source: 'geojson-markers',
				layout: {
					'icon-image': ['get', 'icon'],
					'icon-size': 0.3,
					'icon-anchor': 'bottom',
					'icon-allow-overlap': true
				},
				minzoom: 0,
				maxzoom: 24
			}
		];

		return {
			mapItems,
			sources,
			layers,
			markers,
			attachListeners,
			popupData,
			mapOptions
		};
	},
	// eslint-disable-next-line @milqmedia/milq/page-has-meta
	asyncData({ params, app, route }) {
		return app.$graphClient
			.query({
				query: areaWebcams,
				variables: {
					slug: params.area,
					path: route.path,
					fallback: route.path.replace(params.area, '_area')
				},
				errorPolicy: 'all'
			})
			.then(({ data, errors }) => {
				if (!data.contentpage) {
					return app.httpError(404, null, 'area-webcams-01', errors);
				}
				if (errors) {
					throw errors[0];
				}

				app.interpolater(data.contentpage.meta, ['title', 'description']);
				app.interpolater(data.contentpage.content, ['markdown']);
				app.interpolater(data.contentpage, ['name']);

				app.meta(data.contentpage.meta);
				app.ads({
					navigation: {
						visible: true,
						targets: {
							Skiresort: data.area.id,
							State: data.area.region.id,
							Country: data.area.country.id
						}
					}
				});
				app.areaBreadcrumbs(data.area, {
					[app.i18n.t(`page.areas._area.webcams:breadcrumb`, {
						area: data.area.name
					})]: {
						name: 'areas-area-webcams',
						params: { area: data.area.slug }
					}
				});

				return data;
			})
			.catch(app.$destinationNotFoundCatcher)
			.catch(error => {
				return app.httpError(500, null, 'areas-area-webcams-01', error);
			});
	},
	head() {
		return {
			...this.$structuredData()
		};
	},
	created() {
		this.$emit('update:showSidebar', false);
	},
	mounted() {
		this.mapItems = this.area.webcams.map(webcam => {
			const newWebcam = this.$lodash.cloneDeep(webcam);
			newWebcam.geography = {
				location: {
					type: 'Point',
					coordinates: [webcam.location.x, webcam.location.y]
				}
			};
			return newWebcam;
		});

		if (this.$refs.map && this.mapItems.length > 1) {
			this.$nextTick(() => {
				this.$refs.map.fitToCurrentBounds();
			});
		}
	},
	methods: {
		mouseClick(feature) {
			this.$router.push(
				this.localePath({
					name: 'webcams-webcam',
					params: { webcam: feature.properties.id }
				})
			);
		},
		mapStyle(map) {
			if (this.$refs.map && this.mapItems.length > 1) {
				this.$refs.map.fitToCurrentBounds();
			}
			this.attachListeners(
				map,
				this.$refs.map,
				'markers',
				'geojson-markers',
				this.mouseClick
			);
		}
	}
};
</script>
